import React, { useEffect, useState, useRef, Fragment } from 'react';
/**
 * DragDropFillRenderer — Fill blanks by clicking drag options.
 *
 * Supports two seed data formats:
 * Format A (canonical): template with ___, blanks[{id, correctAnswer}], dragOptions[]
 * Format B (legacy):    template with {0}/{1}, draggables[], correctPlacements{}
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
interface NormalizedBlank {
  id: string;
  correctAnswer: string;
}
/** Normalize both seed formats into a canonical shape */
function normalizeConfig(config: Record<string, unknown>): {
  parts: string[];
  blanks: NormalizedBlank[];
  dragOptions: string[];
} {
  const template: string = config.template as string || '';
  const rawBlanks = config.blanks as Array<{
    id: string;
    correctAnswer: string;
  }> | undefined;
  const draggables = config.draggables as string[] | undefined;
  const correctPlacements = config.correctPlacements as Record<string, number> | undefined;
  // Detect format: if draggables + correctPlacements exist, it's Format B ({0}/{1} placeholders)
  if (draggables && correctPlacements) {
    // Format B: template uses {0}, {1}, etc.
    const placeholderRegex = /\{(\d+)\}/g;
    const ids: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = placeholderRegex.exec(template)) !== null) {
      ids.push(match[1]);
    }
    const parts = template.split(/\{\d+\}/);
    const blanks: NormalizedBlank[] = ids.map(id => ({
      id,
      correctAnswer: draggables[correctPlacements[id] ?? 0] || ''
    }));
    return {
      parts,
      blanks,
      dragOptions: draggables
    };
  }
  // Format A: template uses ___
  const parts = template.split('___');
  const blanks: NormalizedBlank[] = rawBlanks || [];
  // dragOptions can be in config.dragOptions or config.options (some seeds use data.options)
  const dragOptions: string[] = config.dragOptions as string[] || config.options as string[] || [];
  return {
    parts,
    blanks,
    dragOptions
  };
}
export function DragDropFillRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data || {}) as Record<string, unknown>;
  const {
    parts,
    blanks,
    dragOptions
  } = normalizeConfig(config);
  const prevValidateRef = useRef(false);
  const [filledBlanks, setFilledBlanks] = useState<Record<string, string>>({});
  useEffect(() => {
    setFilledBlanks({});
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    const isReady = blanks.every(b => filledBlanks[b.id]);
    onReadyChange?.(isReady);
  }, [filledBlanks, blanks, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(filledBlanks));
    }
  }, [shouldValidate, filledBlanks, onAnswer]);
  const handleOptionClick = (option: string) => {
    const firstEmpty = blanks.find(b => !filledBlanks[b.id]);
    if (firstEmpty) {
      setFilledBlanks(prev => ({
        ...prev,
        [firstEmpty.id]: option
      }));
    }
  };
  const handleBlankClick = (blankId: string) => {
    const next = {
      ...filledBlanks
    };
    delete next[blankId];
    setFilledBlanks(next);
  };
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      document.getElementById(`drag-option-${(index + 1) % dragOptions.length}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      document.getElementById(`drag-option-${(index - 1 + dragOptions.length) % dragOptions.length}`)?.focus();
    }
  };
  return <div className="w-full max-w-2xl mx-auto flex flex-col gap-8" data-id="element-8005">
      {/* Sentence Area */}
      <div className="p-6 bg-indigo-50 rounded-2xl border-2 border-indigo-100 text-xl leading-loose flex flex-wrap items-center gap-2 justify-center" data-id="element-8006">
        {parts.map((part: string, index: number) => <Fragment key={index} data-id="element-8007">
            <span data-id="element-8008">
              <LatexRenderer content={part} data-id="element-8009" />
            </span>
            {index < parts.length - 1 && blanks[index] && <button onClick={() => handleBlankClick(blanks[index].id)} aria-label={`Lege plek ${index + 1}${filledBlanks[blanks[index].id] ? `, gevuld met ${filledBlanks[blanks[index].id]}` : ', leeg'}`} className={`min-w-[100px] h-10 px-3 rounded-lg border-2 border-dashed transition-all flex items-center justify-center font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${filledBlanks[blanks[index].id] ? 'bg-white border-indigo-500 text-indigo-700 shadow-sm' : 'bg-white/50 border-slate-300 text-slate-400'}`} data-id="element-8010">
                {filledBlanks[blanks[index].id] || '?'}
              </button>}
          </Fragment>)}
      </div>

      {/* Options Area */}
      <div className="flex flex-wrap gap-3 justify-center" role="listbox" aria-label="Kies een woord om in te vullen" data-id="element-8011">
        {dragOptions.map((option: string, index: number) => {
        const isUsed = Object.values(filledBlanks).includes(option);
        return <button key={index} id={`drag-option-${index}`} role="option" aria-selected={isUsed} onClick={() => !isUsed && handleOptionClick(option)} onKeyDown={e => handleKeyDown(e, index)} disabled={isUsed} className={`px-4 py-2 rounded-xl border-2 font-medium text-lg transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isUsed ? 'bg-slate-100 border-slate-200 text-slate-300 cursor-default' : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-400 hover:shadow-md hover:-translate-y-0.5'}`} data-id="element-8012">
              <LatexRenderer content={option} data-id="element-8013" />
            </button>;
      })}
      </div>

      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-8014">
        Klik of druk Enter om een woord te plaatsen
      </p>
    </div>;
}